'use client'

import {
  BlankFillingHintFunnel,
  BlankFillingQuizFunnel,
  EnglishMultiChoiceSentenceHintFunnel,
  EnglishMultiChoiceSentenceQuizFunnel,
  EnglishMultiChoiceWordHintFunnel,
  EnglishMultiChoiceWordQuizFunnel,
  JapaneseMultiChoiceSentenceHintFunnel,
  JapaneseMultiChoiceSentenceQuizFunnel,
  JapaneseMultiChoiceWordHintFunnel,
  JapaneseMultiChoiceWordQuizFunnel,
  Question,
  SentenceMakingHintFunnel,
  SentenceMakingQuizFunnel,
} from '@entities/lesson'
import { LessonReviewAnswerStatus } from '@features/lesson-review'
import { classLangTypeSchema } from '@server/schemas'
import { useTranslation } from '@shared/config/i18n'
import { UI_IDS } from '@shared/constants'
import React, { Suspense, useState } from 'react'
import { match, P } from 'ts-pattern'

import { Button, HStack, Typography, VStack } from '@podo-app/design-system-temp'

type Props = {
  question: Question
  value: Undefinable<string>
  canNavigateNextFunnel: boolean
  handleStepValue: (value: string) => void
  status: LessonReviewAnswerStatus
  handleChangeCurrentStatus: (status: LessonReviewAnswerStatus) => void
}

/**
 * @description 진단 객관식 문제에서 정답을 index 로 다루는 이유
 *  진단 문제를 생성할 때 문제와 정답을 모두 llm이 생성함
 *  여기서 정답을 string 으로 생성해달라 하면 정답 체크할 때 문맥상 정답이지만 본문에 있는 string 이랑 llm 생성한 정답 string이 조금이라도 틀릴경우 오답으로 판단해버림
 *  llm 에게 할루시네이션 여지를 주는 행동이라 판단했음
 *  이런이유로 llm에 문제생성을 하는 단계에서부터 객관식의 경우 정답을 인덱스로 리턴함.
 *  결국 진단 시스템 내에서 객관식의 경우 문제 정답이 인덱스라고 확신할수 있기 때문에 적어도 "정답은 문제 보기중 하나다" 라고 확신하고 쓸수 있음
 */
export const LessonReviewFunnelView = ({
  question,
  value,
  canNavigateNextFunnel,
  handleStepValue,
  status,
  handleChangeCurrentStatus,
}: Props) => {
  const t = useTranslation()
  const [isHintOpen, setIsHintOpen] = useState(false)

  const highlightText = (text: string, highlight: string) => {
    if (!highlight) {
      return text
    }

    const parts = text.split(new RegExp(`(${highlight})`, 'gi'))

    return parts.map((part, index) => (
      <React.Fragment key={index}>
        {part.toLowerCase() === highlight.toLowerCase() ? (
          <span className="text-[#6184FF] font-medium">{part}</span>
        ) : (
          part
        )}
      </React.Fragment>
    ))
  }

  return (
    <React.Fragment>
      {match(question)
        .with({ type: 'BLANK_FILLING' }, (blankFillingContext) => (
          <BlankFillingQuizFunnel
            question={blankFillingContext}
            value={value ?? ''}
            handleChangeCurrentFunnelValue={(value) => {
              handleStepValue(value)
              setIsHintOpen(false)
            }}
            canNavigateNextFunnel={canNavigateNextFunnel}
            status={status}
            handleChangeCurrentStatus={handleChangeCurrentStatus}
          />
        ))

        .with({ type: 'SENTENCE_MAKING' }, (sentenceMakingContext) => (
          <SentenceMakingQuizFunnel
            question={sentenceMakingContext}
            value={value ?? ''}
            handleChangeCurrentFunnelValue={(value) => {
              handleStepValue(value)
              setIsHintOpen(false)
            }}
            canNavigateNextFunnel={canNavigateNextFunnel}
            status={status}
            handleChangeCurrentStatus={handleChangeCurrentStatus}
          />
        ))
        .with(
          { type: 'MULTI_CHOICE_WORD', lang_type: P.union(classLangTypeSchema.Enum.EN, classLangTypeSchema.Enum.KR) },
          (multiChoiceWordContext) => (
            <EnglishMultiChoiceWordQuizFunnel
              question={multiChoiceWordContext}
              value={value ?? ''}
              handleChangeCurrentFunnelValue={(value) => {
                handleStepValue(value)
                setIsHintOpen(false)
              }}
            />
          ),
        )
        .with({ type: 'MULTI_CHOICE_WORD', lang_type: classLangTypeSchema.Enum.JP }, (multiChoiceWordContext) => (
          <JapaneseMultiChoiceWordQuizFunnel
            question={multiChoiceWordContext}
            value={value ?? ''}
            contentLevel={multiChoiceWordContext.content_level}
            handleChangeCurrentFunnelValue={(value) => {
              handleStepValue(value)
              setIsHintOpen(false)
            }}
          />
        ))
        .with(
          {
            type: 'MULTI_CHOICE_SENTENCE',
            lang_type: P.union(classLangTypeSchema.Enum.EN, classLangTypeSchema.Enum.KR),
          },
          (multiChoiceSentenceContext) => (
            <EnglishMultiChoiceSentenceQuizFunnel
              question={multiChoiceSentenceContext}
              value={value ?? ''}
              handleChangeCurrentFunnelValue={(value) => {
                handleStepValue(value)
                setIsHintOpen(false)
              }}
            />
          ),
        )
        .with(
          { type: 'MULTI_CHOICE_SENTENCE', lang_type: classLangTypeSchema.Enum.JP },
          (multiChoiceSentenceContext) => (
            <JapaneseMultiChoiceSentenceQuizFunnel
              question={multiChoiceSentenceContext}
              value={value ?? ''}
              contentLevel={multiChoiceSentenceContext.content_level}
              handleChangeCurrentFunnelValue={(value) => {
                handleStepValue(value)
                setIsHintOpen(false)
              }}
            />
          ),
        )
        .exhaustive()}

      {match({ isHintOpen, lang_type: question.lang_type })
        .with({ isHintOpen: true }, () => (
          <VStack className="gap-2">
            <Typography className="text-gray-400" size="h6">
              {t('lessonReview.hint')}
            </Typography>

            <Suspense
              fallback={
                <VStack className="p-5 bg-gray-100 border border-gray-200 rounded-lg gap-2 h-[100px] animate-pulse" />
              }
            >
              <VStack className="p-5 bg-gray-100 border border-gray-200 rounded-lg gap-2">
                {match(question)
                  .with({ type: 'BLANK_FILLING' }, (blankFillingContext) => (
                    <BlankFillingHintFunnel question={blankFillingContext} />
                  ))
                  .with({ type: 'SENTENCE_MAKING' }, (sentenceMakingContext) => (
                    <SentenceMakingHintFunnel question={sentenceMakingContext} highlightText={highlightText} />
                  ))
                  .with(
                    {
                      type: 'MULTI_CHOICE_WORD',
                      lang_type: P.union(classLangTypeSchema.Enum.EN, classLangTypeSchema.Enum.KR),
                    },
                    (multiChoiceWordContext) => (
                      <EnglishMultiChoiceWordHintFunnel
                        question={multiChoiceWordContext}
                        highlightText={highlightText}
                      />
                    ),
                  )
                  .with(
                    { type: 'MULTI_CHOICE_WORD', lang_type: classLangTypeSchema.Enum.JP },
                    (multiChoiceWordContext) => (
                      <JapaneseMultiChoiceWordHintFunnel
                        question={multiChoiceWordContext}
                        highlightText={highlightText}
                      />
                    ),
                  )
                  .with(
                    {
                      type: 'MULTI_CHOICE_SENTENCE',
                      lang_type: P.union(classLangTypeSchema.Enum.EN, classLangTypeSchema.Enum.KR),
                    },
                    (multiChoiceSentenceContext) => (
                      <EnglishMultiChoiceSentenceHintFunnel question={multiChoiceSentenceContext} />
                    ),
                  )
                  .with(
                    { type: 'MULTI_CHOICE_SENTENCE', lang_type: classLangTypeSchema.Enum.JP },
                    (multiChoiceSentenceContext) => (
                      <JapaneseMultiChoiceSentenceHintFunnel question={multiChoiceSentenceContext} />
                    ),
                  )
                  .exhaustive()}
              </VStack>
            </Suspense>
          </VStack>
        ))
        .with({ isHintOpen: false }, () => (
          <HStack className="justify-center">
            <Button
              variant="ghost"
              onClick={() => setIsHintOpen(true)}
              eventParams={{ name: UI_IDS.btn.openReviewHint, location: 'lesson_review', action: 'toggle' }}
            >
              {t('lessonReview.hint')}
            </Button>
          </HStack>
        ))
        .otherwise(() => null)}
    </React.Fragment>
  )
}
